// Platform-admin-only: assign inbound phone numbers to a dealer.
//
// Each dealer holds a list of E.164 numbers Retell/Twilio routes inbound
// calls from. The webhook handler resolves the dealer by looking up
// `dealers.inbound_numbers`. Numbers MUST be unique across all dealers
// (one phone line can't legitimately route to two different dealerships)
// — that's enforced by a Mongo unique index.
//
// "Admin pool" pattern: numbers used for platform-wide testing should
// NOT be assigned to any dealer. They land in /api/calls/orphans and
// are visible to platform admins only.
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { dealersApi } from '../../api';

// E.164 sanity: + then 10-15 digits, no spaces/dashes.
const E164 = /^\+[1-9]\d{9,14}$/;

function parseNumbers(text) {
  return text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function InboundNumbersDialog({ dealer, open, onOpenChange, onSaved }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (dealer) {
      const list = Array.isArray(dealer.inbound_numbers) ? dealer.inbound_numbers : [];
      setText(list.join('\n'));
    }
  }, [dealer]);

  if (!dealer) return null;

  const handleSave = async () => {
    const numbers = parseNumbers(text);
    const bad = numbers.filter((n) => !E164.test(n));
    if (bad.length > 0) {
      toast.error(`Not E.164 format: ${bad.join(', ')} (must start with + and country code)`);
      return;
    }
    // Dedupe within the same dealer's list. Cross-dealer uniqueness is
    // enforced by the backend's unique index — a duplicate landed there
    // surfaces here as a 400/409 from the PATCH.
    const unique = Array.from(new Set(numbers));
    setSaving(true);
    try {
      await dealersApi.update(dealer.id, { inbound_numbers: unique });
      toast.success(`Updated ${dealer.name} — ${unique.length} number${unique.length === 1 ? '' : 's'}`);
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Failed to save inbound numbers');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Inbound numbers — {dealer.name}</DialogTitle>
          <DialogDescription>
            E.164 format, one per line (e.g. <code>+14387985050</code>).
            Calls to these numbers route to this dealer. Numbers must be
            unique across all dealers.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="inbound-numbers-textarea">Phone numbers</Label>
          <textarea
            id="inbound-numbers-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="+14387985050"
            rows={5}
            className="w-full font-mono text-sm rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            data-testid="inbound-numbers-textarea"
          />
          <p className="text-xs text-muted-foreground">
            Leave blank to remove all numbers from this dealer (calls to
            previously-assigned numbers will land in the orphan bucket).
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="inbound-numbers-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
